/**
 * @file form-template/revision.ts
 * @description 表單版本標示（Rev）與版本日期的解析/格式化純函式（2026-09-25 回報序39）。
 *
 * 客戶的表單「版本日期」是表單本身的改版日期（印在檔名/表頭），
 * 例如「A 1ZHSR01-08 有機作物驗證申請表 Rev2 (20240712)」→ Rev2、版本日期 2024-07-12；
 * 不可再用系統新增/修改當天日期充當版本日期。檔案上傳日期另欄保存。
 */
/**
 * 驗證 YYYYMMDD 是否為合法日期，合法時回傳 YYYY-MM-DD。
 * @param ymd 8 位數字字串
 */
function toIsoDate(ymd) {
    const m = /^(\d{4})(\d{2})(\d{2})$/.exec(ymd);
    if (!m)
        return null;
    const [, y, mo, d] = m;
    const year = Number(y);
    const month = Number(mo);
    const day = Number(d);
    if (year < 1990 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31)
        return null;
    const dt = new Date(Date.UTC(year, month - 1, day));
    if (dt.getUTCMonth() !== month - 1)
        return null; // 排除 0231 這類不存在的日期
    return `${y}-${mo}-${d}`;
}
/**
 * 從表單檔名解析版本標示與版本日期。
 *
 * 支援格式（不分大小寫、括號全半形皆可）：
 *  - 「… Rev2 (20240712).docx」「…Rev1.3(20200130)」「…REV 3.0（20210525）」
 *  - 只有日期：「… (20240712)」→ 僅回傳日期
 *  - 版本寫法「V2」「v1.3」亦可（前後需非字母，避免誤抓 ZHSR 內字元）
 *
 * @param filename 原始檔名（可含副檔名）
 */
export function parseRevisionFromFilename(filename) {
    if (!filename)
        return { revisionLabel: null, revisionDate: null };
    // 只剝除已知的檔案副檔名：泛用的 /\.[A-Za-z0-9]{2,5}$/ 會把「Rev1.10」的「.10」誤當副檔名剝掉
    const base = filename.replace(/\.(?:docx?|xlsx?|xlsm|pdf)$/i, '');
    const revMatch = /Rev\.?\s*([0-9]+(?:\.[0-9]+)*)/i.exec(base) ??
        // V 寫法每段限 1–3 位數字，避免把「v20240712」這類日期誤當版本號
        /(?:^|[^A-Za-z])[Vv]\s*([0-9]{1,3}(?:\.[0-9]{1,3})*)(?=[^0-9.]|$)/.exec(base);
    const revisionLabel = revMatch?.[1] ?? null;
    // 版本日期優先取括號內 8 位數字，其次取任一 8 位數字（且須為合法日期）
    let revisionDate = null;
    const bracketed = /[(（]\s*(\d{8})\s*[)）]/.exec(base);
    if (bracketed?.[1])
        revisionDate = toIsoDate(bracketed[1]);
    if (!revisionDate) {
        for (const m of base.matchAll(/(?<!\d)(\d{8})(?!\d)/g)) {
            const iso = m[1] ? toIsoDate(m[1]) : null;
            if (iso) {
                revisionDate = iso;
                break;
            }
        }
    }
    return { revisionLabel, revisionDate };
}
/**
 * 組出列表/選單顯示用的版本字樣：「Rev2 (20240712)」。
 * - revisionLabel 缺省時回落系統版次 version（整數）。
 * - revisionDate 缺省時不顯示日期（不再以系統更新日期頂替）。
 *
 * @param opts.revisionLabel 版本標示（不含 Rev）
 * @param opts.revisionDate 版本日期 YYYY-MM-DD
 * @param opts.version 系統版次（form_template_versions.version）
 */
export function formatRevisionLabel(opts) {
    const label = opts.revisionLabel?.trim() || String(opts.version ?? 1);
    const rev = `Rev${label.replace(/^rev\.?\s*/i, '')}`;
    const date = opts.revisionDate ? opts.revisionDate.slice(0, 10).replace(/-/g, '') : '';
    return date ? `${rev} (${date})` : rev;
}
